import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Users, TrendingUp, Flame, MapPin, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";

interface CrisisZone {
  name: string;
  region: string;
  events24h: number;
  criticalEvents: number;
  estimatedDisplaced: string;
  estimatedCasualties: string;
  escalationProbability: number;
  status: "critical" | "high" | "elevated" | "moderate";
  lastEvent: string;
}

const STATUS_CONFIG = {
  critical: { color: "bg-destructive text-destructive-foreground", label: "CRITICAL", barColor: "bg-destructive" },
  high: { color: "bg-orange-600 text-white", label: "HIGH", barColor: "bg-orange-500" },
  elevated: { color: "bg-yellow-600 text-white", label: "ELEVATED", barColor: "bg-yellow-500" },
  moderate: { color: "bg-blue-600 text-white", label: "MODERATE", barColor: "bg-blue-500" },
};

// Known crisis zones with baseline data (enriched by live events)
const CRISIS_BASELINES: Record<string, { displaced: string; casualties: string; baseEscalation: number }> = {
  "Gaza": { displaced: "1.9M+", casualties: "45,000+", baseEscalation: 85 },
  "Ukraine": { displaced: "6.5M+", casualties: "30,000+", baseEscalation: 72 },
  "Sudan": { displaced: "8.6M+", casualties: "15,000+", baseEscalation: 78 },
  "Yemen": { displaced: "4.5M+", casualties: "21,000+", baseEscalation: 60 },
  "Syria": { displaced: "6.8M+", casualties: "12,000+", baseEscalation: 55 },
  "Lebanon": { displaced: "1.2M+", casualties: "4,000+", baseEscalation: 65 },
  "Myanmar": { displaced: "2.7M+", casualties: "8,000+", baseEscalation: 58 },
  "Somalia": { displaced: "3.8M+", casualties: "5,000+", baseEscalation: 50 },
  "Iran": { displaced: "—", casualties: "—", baseEscalation: 70 },
  "Israel": { displaced: "200K+", casualties: "2,000+", baseEscalation: 80 },
};

const CrisisDashboard = () => {
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);

  const crisisZones = useMemo<CrisisZone[]>(() => {
    if (!events?.length) return [];

    const now = Date.now();
    const countryMap = new Map<string, { total: number; critical: number; lastTime: number }>();

    for (const e of events) {
      const country = e.country || "Unknown";
      const age = now - new Date(e.occurred_at).getTime();
      if (age > 86400000) continue; // 24h only

      const prev = countryMap.get(country) || { total: 0, critical: 0, lastTime: 0 };
      prev.total++;
      if (e.event_type === "missile" || e.event_type === "explosion" || e.event_type === "air_raid") {
        prev.critical++;
      }
      const t = new Date(e.occurred_at).getTime();
      if (t > prev.lastTime) prev.lastTime = t;
      countryMap.set(country, prev);
    }

    const zones: CrisisZone[] = [];
    for (const [country, data] of countryMap) {
      const baseline = CRISIS_BASELINES[country];
      const escalation = baseline
        ? Math.min(99, baseline.baseEscalation + data.critical * 2)
        : Math.min(95, 30 + data.total * 5 + data.critical * 10);

      const status: CrisisZone["status"] =
        escalation >= 80 ? "critical" :
        escalation >= 65 ? "high" :
        escalation >= 45 ? "elevated" : "moderate";

      const minsAgo = Math.floor((now - data.lastTime) / 60000);
      const lastEvent = minsAgo < 60 ? `${minsAgo}m ago` : `${Math.floor(minsAgo / 60)}h ago`;

      zones.push({
        name: country,
        region: getRegion(country),
        events24h: data.total,
        criticalEvents: data.critical,
        estimatedDisplaced: baseline?.displaced || "—",
        estimatedCasualties: baseline?.casualties || "—",
        escalationProbability: escalation,
        status,
        lastEvent,
      });
    }

    return zones
      .sort((a, b) => b.escalationProbability - a.escalationProbability)
      .slice(0, 5);
  }, [events]);

  // Live "last updated" timer
  const [secondsAgo, setSecondsAgo] = useState(0);
  useEffect(() => {
    setSecondsAgo(0);
    const interval = setInterval(() => setSecondsAgo((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [events]);

  if (!crisisZones.length) return null;

  const updatedLabel = secondsAgo < 5 ? "just now" : secondsAgo < 60 ? `${secondsAgo}s ago` : `${Math.floor(secondsAgo / 60)}m ago`;

  return (
    <section className="border-b border-border py-6 md:py-10">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/15">
            <Flame className="h-4 w-4 text-destructive" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-black tracking-tight">Crisis Dashboard</h2>
            <p className="text-xs text-muted-foreground">Top 5 active crises — updated in real-time</p>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-2.5 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
            </span>
            <span className="text-[10px] font-bold text-destructive tabular-nums">
              Updated {updatedLabel}
            </span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {crisisZones.map((zone, i) => {
            const cfg = STATUS_CONFIG[zone.status];
            return (
              <motion.div
                key={zone.name}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <Card className="h-full border-border/50 hover:border-primary/30 transition-colors">
                  <CardHeader className="pb-2 px-3 pt-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-bold">{zone.name}</CardTitle>
                      <Badge className={`${cfg.color} text-[9px] px-1.5 py-0`}>{cfg.label}</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{zone.region}</p>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 space-y-2">
                    {/* Escalation bar */}
                    <div>
                      <div className="flex justify-between text-[10px] mb-0.5">
                        <span className="text-muted-foreground">Escalation</span>
                        <span className="font-bold font-mono">{zone.escalationProbability}%</span>
                      </div>
                      <Progress value={zone.escalationProbability} className="h-1.5" />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="h-2.5 w-2.5 text-destructive" />
                        <span><b>{zone.events24h}</b> events</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-2.5 w-2.5 text-orange-500" />
                        <span><b>{zone.criticalEvents}</b> critical</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-2.5 w-2.5 text-blue-500" />
                        <span>{zone.estimatedDisplaced}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-2.5 w-2.5 text-muted-foreground" />
                        <span>{zone.lastEvent}</span>
                      </div>
                    </div>

                    {zone.estimatedCasualties !== "—" && (
                      <p className="text-[9px] text-muted-foreground/70 border-t border-border/50 pt-1">
                        Est. casualties: {zone.estimatedCasualties}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-2 text-[9px] text-muted-foreground text-center">
          Sources: ACLED, UNHCR, OCHA, UCDP · Casualty & displacement figures are cumulative estimates
        </p>
      </div>
    </section>
  );
};

function getRegion(country: string): string {
  const ME = ["Gaza", "Israel", "Iran", "Yemen", "Syria", "Lebanon", "Iraq"];
  const AF = ["Sudan", "Somalia", "Ethiopia", "Nigeria", "DRC"];
  const AS = ["Myanmar", "Afghanistan", "Pakistan"];
  if (ME.includes(country)) return "Middle East";
  if (AF.includes(country)) return "Africa";
  if (AS.includes(country)) return "Asia";
  if (country === "Ukraine") return "Eastern Europe";
  return "Global";
}

export default CrisisDashboard;
